/**
 * CS: Size Guide page interactions.
 * Web Component — handles sidebar nav, dropdown, tabs, unit toggles, deep linking.
 * Data loading (table generation from JSON) is self-contained.
 */
class CsSizeGuide extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    /* ── Parse all JSON data blocks ── */
    const allData = {};
    this.querySelectorAll('.cs-size-guide__data').forEach((block) => {
      try {
        const parsed = JSON.parse(block.textContent);
        allData[parsed.handle] = parsed;
      } catch (e) {
        console.warn('[SizeGuide] Failed to parse data for:', block.dataset.handle, e);
      }
    });

    /* ── Build all tables ── */
    Object.keys(allData).forEach((handle) => {
      const entry = allData[handle];

      if (entry.bodyData) {
        const bodySizes = this.getSizes(entry.bodyData);
        if (bodySizes) {
          this.generateTable(bodySizes, 'in', 'sg-body-in-' + handle);
          this.generateTable(bodySizes, 'cm', 'sg-body-cm-' + handle);
        }
      }

      if (entry.productData) {
        const productSizes = this.getSizes(entry.productData);
        if (productSizes) {
          this.generateTable(productSizes, 'in', 'sg-product-in-' + handle);
          this.generateTable(productSizes, 'cm', 'sg-product-cm-' + handle);
        }
      }
    });

    /* ── Cache references ── */
    this.sidebar = this.querySelector('.cs-size-guide__sidebar');
    this.dropdown = this.querySelector('#csSizeGuideSelect');
    this.productSections = this.querySelectorAll('.cs-size-guide__product');
    this.contentArea = this.querySelector('.cs-size-guide__content');

    /* ── Sidebar click handler ── */
    if (this.sidebar) {
      this.sidebar.addEventListener('click', (e) => {
        const link = e.target.closest('.cs-size-guide__nav-link');
        if (link) {
          e.preventDefault();
          this.showProductType(link.dataset.handle);
        }
      }, { signal });
    }

    /* ── Dropdown change handler ── */
    if (this.dropdown) {
      this.dropdown.addEventListener('change', () => {
        this.showProductType(this.dropdown.value);
      }, { signal });
    }

    /* ── Waist guide link handler ── */
    const waistGuide = this.querySelector('.cs-size-guide__waist-guide');
    if (waistGuide) {
      waistGuide.addEventListener('click', (e) => {
        const link = e.target.closest('.cs-size-guide__waist-link');
        if (link && link.dataset.handle) {
          e.preventDefault();
          this.showProductType(link.dataset.handle);
          if (this.contentArea) {
            this.contentArea.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, { signal });
    }

    /* ── Tab switching + unit toggles (content area delegation) ── */
    if (this.contentArea) {
      this.contentArea.addEventListener('click', (e) => {
        const tab = e.target.closest('.cs-size-guide__tab');
        if (!tab) return;

        const product = tab.closest('.cs-size-guide__product');
        if (!product) return;

        const targetPanel = tab.dataset.tab;

        product.querySelectorAll('.cs-size-guide__tab').forEach((t) => {
          const isActive = t === tab;
          t.classList.toggle('active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        product.querySelectorAll('.cs-size-guide__panel').forEach((p) => {
          p.style.display = p.dataset.panel === targetPanel ? '' : 'none';
        });
      }, { signal });

      this.contentArea.addEventListener('change', (e) => {
        if (e.target.type !== 'radio') return;
        if (!e.target.name || !e.target.name.startsWith('sg-unit-')) return;

        const panel = e.target.closest('.cs-size-guide__panel');
        if (!panel) return;

        const selectedUnit = e.target.value.toLowerCase();

        panel.querySelectorAll('.cs-size-guide__table-wrap').forEach((wrap) => {
          wrap.style.display = wrap.dataset.unit === selectedUnit ? '' : 'none';
        });
      }, { signal });
    }

    /* ── Deep linking + initial state ── */
    const hash = window.location.hash.replace('#', '');
    let firstHandle = null;

    if (this.sidebar) {
      const firstLink = this.sidebar.querySelector('.cs-size-guide__nav-link');
      if (firstLink) firstHandle = firstLink.dataset.handle;
    } else if (this.dropdown && this.dropdown.options.length > 0) {
      firstHandle = this.dropdown.options[0].value;
    }

    if (hash && this.querySelector('#sg-' + hash)) {
      this.showProductType(hash);
    } else if (firstHandle) {
      this.showProductType(firstHandle);
    }

    /* ── Hash change listener ── */
    window.addEventListener('hashchange', () => {
      const newHash = window.location.hash.replace('#', '');
      if (newHash && this.querySelector('#sg-' + newHash)) {
        this.showProductType(newHash);
      }
    }, { signal });
  }

  getSizes(data) {
    if (!data || typeof data !== 'object') return null;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch (e) { return null; }
    }
    const keys = Object.keys(data);
    if (keys.length === 0) return null;
    const first = data[keys[0]];
    return first && first.sizes ? first.sizes : null;
  }

  generateTable(sizes, unit, tableId) {
    const table = this.querySelector('#' + tableId);
    if (!table || !sizes || sizes.length === 0) return;

    table.innerHTML = '';

    const measurementKeys = [];
    const keySet = {};
    sizes.forEach((sizeData) => {
      if (!sizeData.measurements) return;
      Object.keys(sizeData.measurements).forEach((key) => {
        if (!keySet[key]) {
          keySet[key] = true;
          measurementKeys.push(key);
        }
      });
    });

    if (measurementKeys.length === 0) return;

    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    const sizeHeader = document.createElement('th');
    sizeHeader.textContent = 'Size';
    headerRow.appendChild(sizeHeader);

    measurementKeys.forEach((key) => {
      const th = document.createElement('th');
      th.textContent = key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    sizes.forEach((sizeData) => {
      const row = document.createElement('tr');
      const sizeCell = document.createElement('td');
      sizeCell.textContent = sizeData.size;
      row.appendChild(sizeCell);

      measurementKeys.forEach((key) => {
        const dataCell = document.createElement('td');
        const measurement = sizeData.measurements && sizeData.measurements[key]
          ? sizeData.measurements[key][unit]
          : null;
        dataCell.textContent = measurement != null ? measurement : 'N/A';
        row.appendChild(dataCell);
      });

      tbody.appendChild(row);
    });

    table.appendChild(tbody);
  }

  showProductType(handle) {
    let found = false;

    this.productSections.forEach((section) => {
      if (section.dataset.productType === handle) {
        section.style.display = '';
        found = true;
      } else {
        section.style.display = 'none';
      }
    });

    if (this.sidebar) {
      this.sidebar.querySelectorAll('.cs-size-guide__nav-link').forEach((link) => {
        const isActive = link.dataset.handle === handle;
        link.classList.toggle('active', isActive);
        link.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    if (this.dropdown) {
      this.dropdown.value = handle;
    }

    if (found && history.replaceState) {
      history.replaceState(null, '', '#' + handle);
    }
  }

  disconnectedCallback() {
    this.abort?.abort();
  }
}
customElements.define('cs-size-guide', CsSizeGuide);
